import React, { useState } from 'react';

import {
  Datagrid,
  DateField,
  ReferenceField,
  ReferenceManyField,
  SelectField,
  TextField,
  usePermissions,
  useRecordContext,
  useResourceContext,
} from 'react-admin';
import { Route, Routes, useParams } from 'react-router-dom';

import {
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  Slide,
  Typography,
} from '@mui/material';

import hasAccess from '../../utilities/hasAccess';

import GetFormLink from './GetFormLink';
import InfoPopover from '../layout/InfoPopover';
import NoResults from '../layout/NoResults';
import Pagination from '../layout/Pagination';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import { kycFormStatusesChoices } from '../../constants/kycFormStatuses';
import actions, { Actions } from '../../constants/actions';
import entityTypes, { entityTypesFromResource, isValidEntityType } from '../../constants/entityTypes';
import formRelatedEntities, { FormRelatedEntities } from '../../constants/formRelatedEntities';
import KycFormView from './KycFormView';
import FormAnswerView from './view';
import ConflictResolution from '../conflict/ConflictResolution';

import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import SelectInputButton from '../layout/inputFields/SelectInputButton';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';

const ConflictResolutionPage = () => {
  const { entityId, entityType } = useParams();

  if (!entityId || !entityType || !isValidEntityType(entityType)) return null;

  return (
    <ConflictResolution entityId={entityId} entityType={entityType} />
  );
};

const KycForm = () => {
  const { permissions } = usePermissions<Actions[]>();
  const [
    relatedEntities,
    setRelatedEntities,
  ] = useState<FormRelatedEntities>(formRelatedEntities.ALL);

  const resource = useResourceContext();
  const record = useRecordContext<{ id: string }>();

  if (!resource || !record) return null;

  const entityType = entityTypesFromResource[resource];

  const relatedEntitiesChoices: {
    id: FormRelatedEntities;
    name: string;
  }[] = [
    { id: formRelatedEntities.ALL, name: 'All' },
    { id: formRelatedEntities.SELF, name: entityType },
  ];

  if (entityType === entityTypes.ACCOUNT) {
    relatedEntitiesChoices.push({ id: formRelatedEntities.RELATED_PRODUCTS, name: 'Related products' });
  } else if (entityType === entityTypes.INDIVIDUAL || entityType === entityTypes.BUSINESS) {
    relatedEntitiesChoices.push(
      { id: formRelatedEntities.RELATED_ACCOUNTS, name: 'Related accounts' },
      { id: formRelatedEntities.RELATED_PRODUCTS, name: 'Related products' },
    );
  }

  return (
    <Routes>
      <Route
        index
        element={(
          <Slide in direction="right" appear={false}>
            <Grid container>
              <Grid size={12}>
                <Card variant="outlined">
                  <CardHeader
                    title="KYC forms"
                    action={(
                      <Box display="flex" flexWrap="wrap" gap={2}>
                        {(
                          entityType === entityTypes.INDIVIDUAL
                          || entityType === entityTypes.BUSINESS
                          || entityType === entityTypes.ACCOUNT
                        ) && (
                          <SelectInputButton
                            choices={relatedEntitiesChoices}
                            value={relatedEntities}
                            setValue={setRelatedEntities}
                          />
                        )}
                        {hasAccess(permissions, actions.FORM_GET_LINK) && <GetFormLink />}
                      </Box>
                    )}
                  />
                  <Divider />
                  <ReferenceManyField
                    reference="form-answers"
                    target="entity.id"
                    perPage={50}
                    pagination={<Pagination disableEmptyText />}
                    filter={{
                      relatedEntities: entityType === entityTypes.PRODUCT
                        ? formRelatedEntities.SELF : relatedEntities,
                      'entity.type': entityType,
                    }}
                  >
                    <Datagrid
                      sx={boldDataGridStyle}
                      bulkActionButtons={false}
                      empty={<NoResults />}
                    >
                      {hasAccess(permissions, actions.FORM_GET) && (
                        <ReferenceField emptyText="Manual" source="formId" sortable={false} reference="forms" label="Name">
                          <TextField source="title" />
                        </ReferenceField>
                      )}
                      <ReferenceEntityField source="entity" sortable={false} label="Entity">
                        <EntityDisplayNameField />
                      </ReferenceEntityField>
                      <DateField
                        source="createdAt"
                        showTime
                        sortable={false}
                        label={(
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" fontWeight="bold">Created At</Typography>
                            <InfoPopover title="Created At" content="The date and time when the Form Answer was created in the Pingwire system." />
                          </Box>
                        )}
                      />
                      <DateField
                        source="submittedAt"
                        showTime
                        sortable={false}
                        label={(
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" fontWeight="bold">Submitted At</Typography>
                            <InfoPopover title="Submitted At" content="The date and time when the Form Answer was submitted by the customer." />
                          </Box>
                        )}
                      />
                      <SelectField source="status" sortable={false} label="Status" choices={kycFormStatusesChoices} emptyText="-" />
                      {hasAccess(permissions, actions.FORM_GET) && (
                        <KycFormView />
                      )}
                    </Datagrid>
                  </ReferenceManyField>
                </Card>
              </Grid>
            </Grid>
          </Slide>
        )}
      />
      <Route
        path="/:formId/:kycFormId/:formPage/*"
        element={(
          <Routes>
            <Route index element={<FormAnswerView />} />
            <Route path="conflict-resolution/:entityType/:entityId/*" element={<ConflictResolutionPage />} />
          </Routes>
        )}
      />
    </Routes>
  );
};

export default KycForm;
